import { useContext, useEffect, useState } from 'react';
import { LanguageContext } from '../Components/LanguageContext';
import AnimatedComponent from './AnimatedComponent';
import productRangeData from '../lang/productRange.json';
import handleTransformLang from '../functions/handleTransformLang';

const DEFAULT_MOBILE_OPEN_INDEX = -1;

function getIterableData(data) {
  return Object.values(data).map((product) => handleTransformLang(product));
}

export default function ProductRange({ invert = 'false' }) {
  const { language } = useContext(LanguageContext);
  const [productRanges, setProductRanges] = useState(getIterableData(productRangeData));
  const [openIndex, setOpenIndex] = useState(DEFAULT_MOBILE_OPEN_INDEX);

  useEffect(() => {
    setProductRanges(getIterableData(productRangeData));
  }, [language]);

  return (
    <div className="mx-auto w-full max-w-8xl md:px-4">
      <div className="hidden 1.5xl:grid 1.5xl:grid-cols-4 1.5xl:py-11">
        {productRanges?.map((productRangeItem, index) => (
          <AnimatedComponent
            key={`${productRangeItem.title}-desktop`}
            direction="animate-fade-up"
            className={`min-h-fit border-s px-7 [&:nth-child(4n+1)]:border-s-0 [&:nth-child(n+5)]:mt-20 ${
              invert === 'true' ? 'border-white/40' : 'border-[#dadada]'
            }`}
          >
            <div className="flex items-start justify-start pb-2">
              <img
                className="max-h-12 object-contain"
                src={invert === 'true' ? productRangeItem.invertImage : productRangeItem.image}
                alt=""
              />
            </div>
            <div className="flex flex-col justify-start whitespace-pre-line">
              <h3 className={invert === 'true' ? 'pb-3 text-3xl font-bold text-text_primary' : 'pb-3 text-3xl font-bold text-text_tertiary'}>
                {productRangeItem.title}
              </h3>
              <p className={invert === 'true' ? 'text-lg leading-8 text-white' : 'text-lg leading-8 text-text_secondary'}>
                {productRangeItem.description}
              </p>
            </div>
          </AnimatedComponent>
        ))}
      </div>

      <div className="hidden md:grid md:grid-cols-2 md:gap-y-12 md:py-10 lg:grid-cols-3 xl:gap-y-16 xl:py-12 1.5xl:hidden">
        {productRanges?.map((productRangeItem, index) => (
          <AnimatedComponent
            key={productRangeItem.title}
            direction="animate-fade-up"
            className={`min-h-fit border-[#ececec] px-6 md:[&:nth-child(2n+1)]:border-s-0 md:[&:nth-child(n+3)]:pt-8 md:[&:nth-child(2n+2)]:border-s lg:[&:nth-child(2n+2)]:border-s lg:[&:nth-child(2n+1)]:border-s-0 lg:[&:nth-child(3n+1)]:border-s-0 lg:[&:nth-child(n+4)]:pt-10 xl:px-9 ${
              index > 0 ? 'border-s' : ''
            }`}
          >
            <div className="pb-2 flex justify-start items-start">
              <img
                className="max-h-10 object-contain"
                src={invert === 'true' ? productRangeItem.invertImage : productRangeItem.image}
                alt=""
              />
            </div>
            <div className="flex flex-col justify-start whitespace-pre-line">
              <h3 className={invert === 'true' ? 'pb-3 text-[2rem] font-bold text-text_primary' : 'pb-3 text-[2rem] font-bold text-text_tertiary'}>
                {productRangeItem.title}
              </h3>
              <p className={invert === 'true' ? 'text-[17px] leading-8 text-white' : 'text-[17px] leading-8 text-text_secondary'}>
                {productRangeItem.description}
              </p>
            </div>
          </AnimatedComponent>
        ))}
      </div>

      <div className="md:hidden">
        {productRanges?.map((productRangeItem, index) => {
          const isOpen = openIndex === index;
          const descriptionLines = productRangeItem.description.split('\n').filter(Boolean);

          return (
            <AnimatedComponent
              key={productRangeItem.title}
              direction="animate-fade-up"
              className="border-b border-[#ece8e1]"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-3 px-4 py-4 text-left"
              >
                <img
                  className="h-6 w-6 shrink-0 object-contain"
                  src={invert === 'true' ? productRangeItem.invertImage : productRangeItem.image}
                  alt=""
                />
                <span className={`flex-1 text-[18px] font-medium ${invert === 'true' ? 'text-text_primary' : 'text-text_tertiary'}`}>
                  {productRangeItem.title}
                </span>
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  className={`h-5 w-5 shrink-0 transition-transform duration-300 ${invert === 'true' ? 'text-text_primary' : 'text-text_secondary'} ${isOpen ? 'rotate-180' : ''}`}
                >
                  <path
                    d="M5 7.5 10 12.5 15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div
                className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
              >
                <div className="overflow-hidden">
                  <div className={`${invert === 'true' ? 'bg-bg_secondary' : 'bg-[#f2f2f2]'} px-8 py-4`}>
                  <ul className={`space-y-1 text-[15px] leading-7 ${invert === 'true' ? 'text-white' : 'text-text_secondary'}`}>
                    {descriptionLines.map((line) => (
                      <li key={line}>• {line}</li>
                    ))}
                  </ul>
                </div>
                </div>
              </div>
            </AnimatedComponent>
          );
        })}
      </div>
    </div>
  );
}
